/**
 * IntakeRecordingPlayback — V-OCIS-Intake-Playback-Batch-v1.
 *
 * Renders inside <ThreadEventItem> when an `intake_audio_uploaded`
 * event carries an `artifact_id` payload key. Renders a single
 * "Load recording" button until the advisor clicks it; on click,
 * mints a short-lived signed URL via the `get-intake-recording-
 * signed-url` edge function and swaps in a native <audio controls>
 * element streaming from that URL.
 *
 * Design constraints (per batch spec):
 *   - Do NOT auto-fetch on render. The URL is short-lived and we
 *     don't want to mint it for every timeline render — only when
 *     the user signals intent.
 *   - Do NOT show the storage path. The audio element's `src` is
 *     visible in devtools but that's a one-time, expiring URL — a
 *     fundamentally different exposure than a permanent path.
 *   - Show small metadata badges only (provider, duration, size).
 *   - Failure shows an inline error + a Retry button.
 */

import { useState } from 'react';
import { Loader2, Mic, AlertCircle, Play } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface IntakeRecordingPlaybackProps {
  shopId: string;
  ticketId: string;
  artifactId: string;
  /**
   * Optional hint payload fields rendered as inert badges BEFORE the
   * signed URL is fetched. They come from the OCIS event's payload and
   * therefore expose only metadata that the attach hook intentionally
   * publishes — never transcript text, never PII.
   */
  hintProvider?: string | null;
  hintDurationMs?: number | null;
  hintSizeBytes?: number | null;
  hintMimeType?: string | null;
}

interface SignedUrlResponse {
  ok: true;
  signed_url: string;
  expires_in_seconds: number;
  artifact: {
    id: string;
    mime_type: string | null;
    size_bytes: number | null;
    created_at: string | null;
    metadata: Record<string, unknown> | null;
  };
}

function formatBytes(bytes: number | null | undefined): string | null {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(ms: number | null | undefined): string | null {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function IntakeRecordingPlayback({
  shopId,
  ticketId,
  artifactId,
  hintProvider,
  hintDurationMs,
  hintSizeBytes,
  hintMimeType,
}: IntakeRecordingPlaybackProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resolvedMime, setResolvedMime] = useState<string | null>(null);
  const [resolvedDurationMs, setResolvedDurationMs] = useState<number | null>(null);
  const [resolvedSizeBytes, setResolvedSizeBytes] = useState<number | null>(null);

  const handleLoad = async () => {
    if (!shopId || !ticketId || !artifactId) {
      setErrorMessage('Missing context — cannot load recording.');
      setState('error');
      return;
    }
    setState('loading');
    setErrorMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        'get-intake-recording-signed-url',
        {
          body: {
            shop_id: shopId,
            ticket_id: ticketId,
            artifact_id: artifactId,
          },
        },
      );
      if (error) {
        const msg =
          (data as { error?: string } | null)?.error ||
          error.message ||
          'Failed to load recording';
        throw new Error(msg);
      }
      if (!data) throw new Error('get-intake-recording-signed-url returned no data');
      const payload = data as { ok?: boolean; error?: string } & Partial<SignedUrlResponse>;
      // `ok` is a success-only literal (`true`) in the response contract, so
      // it is `true | undefined` here; treat anything but an explicit `true`
      // (or a missing URL) as failure.
      if (payload.ok !== true || !payload.signed_url) {
        throw new Error(payload.error ?? 'Signed URL response missing');
      }
      setSignedUrl(payload.signed_url);
      // Server-resolved metadata wins over event-payload hints.
      const artMime = payload.artifact?.mime_type ?? null;
      const artSize = payload.artifact?.size_bytes ?? null;
      const artMeta = payload.artifact?.metadata as
        | { intake?: { duration_ms?: number | null; provider?: string | null } }
        | null
        | undefined;
      setResolvedMime(artMime ?? hintMimeType ?? null);
      setResolvedSizeBytes(artSize ?? hintSizeBytes ?? null);
      setResolvedDurationMs(artMeta?.intake?.duration_ms ?? hintDurationMs ?? null);
      setState('ready');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      setState('error');
    }
  };

  // Metadata badges before load come from event payload hints; after
  // load they prefer server-resolved values.
  const displayProvider = hintProvider ?? null;
  const displayDuration = formatDuration(
    state === 'ready' ? resolvedDurationMs : hintDurationMs,
  );
  const displaySize = formatBytes(
    state === 'ready' ? resolvedSizeBytes : hintSizeBytes,
  );
  const displayMime = state === 'ready' ? resolvedMime : hintMimeType;

  return (
    <div
      className="mt-1.5 rounded-lg border bg-muted/30 p-2 space-y-2"
      data-testid="intake-recording-playback"
    >
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Mic className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium">Intake recording</span>
        {displayProvider && <Badge variant="outline">{displayProvider}</Badge>}
        {displayDuration && <Badge variant="outline">{displayDuration}</Badge>}
        {displaySize && <Badge variant="outline">{displaySize}</Badge>}
        {displayMime && state === 'ready' && (
          <Badge variant="outline" className="hidden sm:inline-flex">{displayMime}</Badge>
        )}
      </div>

      {state !== 'ready' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleLoad}
            disabled={state === 'loading'}
            data-testid="intake-recording-load-button"
          >
            {state === 'loading' ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Play className="mr-1 h-3 w-3" />
            )}
            {state === 'error' ? 'Retry' : state === 'loading' ? 'Loading…' : 'Load recording'}
          </Button>
          {state === 'error' && errorMessage && (
            <span
              className="inline-flex items-center gap-1 text-xs text-destructive"
              data-testid="intake-recording-error"
            >
              <AlertCircle className="h-3 w-3" />
              {errorMessage}
            </span>
          )}
        </div>
      )}

      {state === 'ready' && signedUrl && (
        <audio
          src={signedUrl}
          controls
          className="w-full"
          data-testid="intake-recording-audio"
        />
      )}
    </div>
  );
}

export default IntakeRecordingPlayback;
